/**
 * Password encrypt/decrypt and verification.
 * Passwords are stored encrypted (AES) so they can be retrieved and shown when needed.
 * Legacy: bcrypt hashes and plain text are still supported for verification.
 */

import CryptoJS from "crypto-js";
import bcrypt from "bcryptjs";

const BCRYPT_ROUNDS = 10;

function getEncryptionKey() {
  return (
    (typeof import.meta !== "undefined" && import.meta.env?.VITE_ENCRYPTION_KEY) ||
    "valence-erp-default-key-change-in-production"
  );
}

/** Check if a stored value looks like our AES ciphertext (CryptoJS format). */
function isEncrypted(stored) {
  if (typeof stored !== "string" || !stored) return false;
  return stored.length > 20 && !stored.startsWith("$2");
}

/** Check if a stored value is a bcrypt hash. */
function isBcryptHash(stored) {
  if (typeof stored !== "string" || !stored) return false;
  return stored.startsWith("$2a$") || stored.startsWith("$2b$") || stored.startsWith("$2y$");
}

/** Encrypt a plain password for storage. */
export function encryptPassword(plainPassword) {
  if (!plainPassword || typeof plainPassword !== "string") return null;
  const key = getEncryptionKey();
  return CryptoJS.AES.encrypt(plainPassword.trim(), key).toString();
}

/** Decrypt a stored password for display. Returns plain text or null if decryption fails. */
export function decryptPassword(cipherText) {
  if (!cipherText || typeof cipherText !== "string") return null;
  if (!isEncrypted(cipherText)) return null;
  try {
    const key = getEncryptionKey();
    const bytes = CryptoJS.AES.decrypt(cipherText, key);
    const plain = bytes.toString(CryptoJS.enc.Utf8);
    return plain || null;
  } catch {
    return null;
  }
}

/** Alias for storage: use encryption (so passwords can be retrieved). */
export function hashPassword(plainPassword) {
  return encryptPassword(plainPassword);
}

/**
 * Verify a plain password against stored value (encrypted, bcrypt, or legacy plain text).
 * Returns true if the password matches.
 */
export function verifyPassword(plainPassword, stored) {
  if (!plainPassword || stored == null) return false;
  const plain = plainPassword.trim();
  if (isEncrypted(stored)) {
    const decrypted = decryptPassword(stored);
    return decrypted !== null && decrypted === plain;
  }
  if (isBcryptHash(stored)) {
    return bcrypt.compareSync(plain, stored);
  }
  return stored === plain;
}
